import { useCallback, useEffect, useRef, useState } from 'react';
import { getAccountLanguage, saveAccountLanguage } from './account-language-api';

type Language = 'en' | 'de';
const pendingKey = 'bookish.pendingGuestLanguage';
const valid = (value: string | null): value is Language => value === 'en' || value === 'de';
const tokenFromStorage = () => { try { return localStorage.getItem('token'); } catch { return null; } };

export function useAccountLanguage(preference: Language, isAdmin: boolean, setPreference: (language: Language) => void) {
  const [, refreshSession] = useState(0);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'error'>('idle');
  const token = tokenFromStorage();
  const session = useRef(token);
  session.current = token;
  const active = useRef(!isAdmin);
  active.current = !isAdmin;
  const revision = useRef(0);
  const currentPreference = useRef(preference);
  currentPreference.current = preference;
  const writes = useRef(Promise.resolve());

  useEffect(() => {
    active.current = !isAdmin;
    const refresh = (event: StorageEvent) => { if (!event.key || event.key === 'token') refreshSession(value => value + 1); };
    window.addEventListener('storage', refresh);
    return () => { window.removeEventListener('storage', refresh); active.current = false; };
  }, [isAdmin]);

  const persist = useCallback((ownerToken: string, language: Language, version: number) => {
    setSaveStatus('saving');
    // Serialize writes so a slower earlier selection cannot overwrite the last choice.
    writes.current = writes.current.catch(() => {}).then(async () => {
      if (session.current !== ownerToken || !active.current) return;
      try {
        await saveAccountLanguage(ownerToken, language);
        if (session.current !== ownerToken || revision.current !== version || !active.current) return;
        try { sessionStorage.removeItem(pendingKey); } catch { /* Browser storage is optional. */ }
        setSaveStatus('idle');
      } catch {
        if (session.current === ownerToken && revision.current === version && active.current) setSaveStatus('error');
      }
    });
  }, []);

  useEffect(() => {
    setSaveStatus('idle');
    if (!token || isAdmin) return;
    const controller = new AbortController();
    const version = revision.current;
    let guestChoice: string | null = null;
    try { guestChoice = sessionStorage.getItem(pendingKey); } catch { /* Browser storage is optional. */ }
    const requested = new URLSearchParams(window.location.search).get('lang');
    try { sessionStorage.removeItem(pendingKey); } catch { /* Consume the guest choice once. */ }
    if (valid(guestChoice) && guestChoice === currentPreference.current && (!valid(requested) || requested === guestChoice)) {
      setPreference(guestChoice);
      persist(token, guestChoice, version);
      return;
    }
    if (valid(requested)) return;
    getAccountLanguage(token, controller.signal).then(language => {
      if (!controller.signal.aborted && session.current === token && revision.current === version && language) setPreference(language);
    }).catch(() => { /* Keep local UI working if the account service cannot be reached. */ });
    return () => controller.abort();
  }, [token, isAdmin, persist, setPreference]);

  const chooseLanguage = useCallback((language: Language) => {
    if (isAdmin || !valid(language)) return;
    revision.current += 1;
    setPreference(language);
    if (token) persist(token, language, revision.current);
    else {
      try { sessionStorage.setItem(pendingKey, language); } catch { /* The local preference still works. */ }
    }
  }, [isAdmin, token, persist, setPreference]);

  return { chooseLanguage, saveStatus: isAdmin ? 'idle' as const : saveStatus };
}
